import React, { useState } from 'react'
import { ConfirmationDialog, Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FormProvider, UseFieldArrayRemove, useForm } from 'react-hook-form'
import { Moment } from 'moment'
import { Button, Grid, Stack, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { useApp } from 'src/Hooks'
import { DynamicTariffDatePicker } from '../form/DateFrameList'
import { firstDayOfALeapYear, lastDayOfALeapYear } from '../../constants'
import { zodResolver } from '@hookform/resolvers/zod'
import { useValidateDateRangeModalForm } from '../../hooks'

export interface DynamicTariffSettingsDateRangeModalProps {
  onClose?: () => void
  place?: number
  from?: Moment
  to?: Moment
  updateDateRange: (from?: Moment, to?: Moment) => void
  removeDateRange: UseFieldArrayRemove
}

interface FormValues {
  from?: Moment
  to?: Moment
}

/**
 * Modal to set and edit date range for dynamic tariff.
 * user can only select month and day for a leap year (1972) so that February is always 29 days.
 * user don't see the year.
 */
const DynamicTariffSettingsDateRangeModal: React.FC<DynamicTariffSettingsDateRangeModalProps> = ({
  onClose,
  from,
  to,
  updateDateRange,
  removeDateRange,
}) => {
  const { t } = useCustomTranslation()
  const { isMobile: isDownSm } = useApp()
  const { dateRangeModalFormSchema } = useValidateDateRangeModalForm()
  const methods = useForm<FormValues>({
    resolver: zodResolver(dateRangeModalFormSchema),
    defaultValues: { from, to },
    mode: 'onSubmit',
  })
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const {
    getValues,
    formState: { isValid },
  } = methods
  const isDisabled = !isValid

  const handleSave = (): void => {
    const [from, to] = getValues(['from', 'to'])
    updateDateRange(from, to)
    onClose?.()
  }

  const handleRemove = (): void => {
    setConfirmationOpen(true)
  }
  const modalActions = {
    primary: {
      name: t('common.save'),
      onClick: handleSave,
      disabled: isDisabled,
    },
    secondary: { name: t('common.cancel') },
  }

  const mobileLayout = (
    <>
      <Grid container>
        <Grid display="flex" alignItems="center" item xs={2}>
          <Typography variant="body1">{t('common.from')}</Typography>
        </Grid>
        <Grid item xs={10}>
          <DynamicTariffDatePicker name="from" defaultCalendarMonth={firstDayOfALeapYear} />
        </Grid>
        <Grid display="flex" alignItems="center" item xs={2}>
          <Typography variant="body1">{t('common.to')}</Typography>
        </Grid>
        <Grid item xs={10}>
          <DynamicTariffDatePicker name="to" defaultCalendarMonth={lastDayOfALeapYear} />
        </Grid>
      </Grid>
      <Button sx={{ mt: theme => theme.spacing(2) }} onClick={handleRemove} variant="text">
        {t('dynamic-tariff-settings.date-range-modal.remove-date-range')}
      </Button>
    </>
  )

  const desktopLayout = (
    <>
      <Grid container>
        <Grid item xs={6}>
          <Typography variant="body1">{t('common.from')}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1">{t('common.to')}</Typography>
        </Grid>
        <Grid item xs={6}>
          <DynamicTariffDatePicker name="from" defaultCalendarMonth={firstDayOfALeapYear} />
        </Grid>
        <Grid item xs={6}>
          <DynamicTariffDatePicker name="to" defaultCalendarMonth={lastDayOfALeapYear} />
        </Grid>
      </Grid>
      <Button sx={{ mt: theme => theme.spacing(2) }} onClick={handleRemove} variant="text">
        {t('dynamic-tariff-settings.date-range-modal.remove-date-range')}
      </Button>
    </>
  )

  return (
    <>
      {confirmationOpen && (
        <ConfirmationDialog
          onClose={() => setConfirmationOpen(false)}
          onCancel={() => setConfirmationOpen(false)}
          onConfirm={() => {
            setConfirmationOpen(false)
            removeDateRange()
            onClose?.()
          }}
          text={t('dialog.confirmDelete.text')}
          title={t('dialog.confirmDelete.title')}
          open={confirmationOpen}
        />
      )}
      <FormProvider {...methods}>
        <Dialog title={t('dynamic-tariff-settings.date-range-modal.title.edit')} onClose={onClose} actions={modalActions} maxWidth="sm">
          <LocalizationProvider dateAdapter={AdapterMoment} dateFormats={{ monthAndYear: 'MMMM' }}>
            <Stack sx={{ mt: theme => theme.spacing(4) }} gap={2}>
              {isDownSm ? mobileLayout : desktopLayout}
            </Stack>
          </LocalizationProvider>
        </Dialog>
      </FormProvider>
    </>
  )
}

export default DynamicTariffSettingsDateRangeModal
